import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import {
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';

import DadosUsuario from '../utils/context';
import { getSectionKeyByPath, getSectionPath, getVisibleSections } from '../utils/menuSections';

const expandedWidth = 248;
const collapsedWidth = 84;

function Sidebar () {
  const location = useLocation();
  const navigate = useNavigate();
  const { autorizacoes = [] } = useContext(DadosUsuario);
  const [collapsed, setCollapsed] = useState(false);

  const visibleSections = useMemo(() => getVisibleSections(autorizacoes), [autorizacoes]);

  const selectedSection = useMemo(() => getSectionKeyByPath(location.pathname), [location.pathname]);

  useEffect(() => {
    const offset = collapsed ? collapsedWidth : expandedWidth;
    document.documentElement.style.setProperty('--drawer-offset', `${offset}px`);

    return () => {
      document.documentElement.style.setProperty('--drawer-offset', '0px');
    };
  }, [collapsed]);

  const itemSx = (active) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '16px',
    mx: 1,
    mb: 0.5,
    pl: active ? 2 : 1.7,
    color: active ? 'var(--app-sidebar-active-text)' : 'var(--app-muted)',
    bgcolor: active ? 'var(--app-sidebar-active-bg)' : 'transparent',
    boxShadow: active ? 'var(--app-sidebar-active-shadow)' : 'none',
    border: '1px solid',
    borderColor: active ? 'var(--app-sidebar-active-ring)' : 'transparent',
    transition: 'background-color 0.18s ease, color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '8px auto 8px 7px',
      width: 4,
      borderRadius: 999,
      backgroundColor: active ? 'var(--setta-amber)' : 'transparent',
      transition: 'background-color 0.18s ease, opacity 0.18s ease'
    },
    '&.Mui-selected': {
      bgcolor: 'var(--app-sidebar-active-bg)',
      color: 'var(--app-sidebar-active-text)'
    },
    '&.Mui-selected:hover': {
      bgcolor: 'var(--app-sidebar-active-hover-bg)'
    },
    '&:hover': {
      bgcolor: active ? 'var(--app-sidebar-active-hover-bg)' : 'rgba(255, 163, 0, 0.08)',
      transform: 'translateX(2px)'
    }
  });

  return (
    <Drawer
      anchor='left'
      open
      variant='permanent'
      sx={{
        width: collapsed ? collapsedWidth : expandedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? collapsedWidth : expandedWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid var(--app-line)',
          background: 'var(--app-sidebar-bg)',
          color: 'var(--app-text)',
          backdropFilter: 'blur(22px)',
          transition: 'width 0.25s ease'
        }
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction='row' alignItems='center' justifyContent={collapsed ? 'center' : 'space-between'} sx={{ px: 1.2, py: 1.3 }}>
          {!collapsed && <Typography sx={{ fontFamily: 'var(--app-font-display)', fontWeight: 800, color: 'var(--app-text)', letterSpacing: 0 }}>MENU</Typography>}
          <IconButton onClick={() => setCollapsed((prev) => !prev)}>
            <MenuRoundedIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ borderColor: 'var(--app-line)' }} />

        <List sx={{ pt: 1 }}>
          <Tooltip title={collapsed ? 'Home' : ''} placement='right'>
            <ListItemButton selected={selectedSection === 'home'} onClick={() => navigate('/')} sx={itemSx(selectedSection === 'home')}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}><HomeRoundedIcon /></ListItemIcon>
              {!collapsed && <ListItemText primary='Home' primaryTypographyProps={{ fontFamily: 'var(--app-font-body)', fontSize: '0.9rem', fontWeight: 800 }} />}
            </ListItemButton>
          </Tooltip>

          {visibleSections.map((section) => {
            const Icon = section.icon;
            const active = selectedSection === section.key;
            return (
              <Tooltip key={section.key} title={collapsed ? section.title : ''} placement='right'>
                <ListItemButton onClick={() => navigate(getSectionPath(section))} selected={active} sx={itemSx(active)}>
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}><Icon /></ListItemIcon>
                  {!collapsed && <ListItemText primary={section.title} primaryTypographyProps={{ fontFamily: 'var(--app-font-body)', fontSize: '0.9rem', fontWeight: 800 }} />}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Stack>
    </Drawer>
  );
}

export default Sidebar;
